import { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const GCLOUD_COMMANDS = `# 1) Create SA
gcloud iam service-accounts create qfid-reader \\
  --display-name="Quantifaid Read"

# 2) Grant roles
gcloud projects add-iam-policy-binding acme-prod \\
  --member='serviceAccount:qfid-reader@acme-prod.iam.gserviceaccount.com' \\
  --role='roles/viewer'

gcloud projects add-iam-policy-binding acme-prod \\
  --member='serviceAccount:qfid-reader@acme-prod.iam.gserviceaccount.com' \\
  --role='roles/asset.viewer'

# 3) Allow Quantifaid to impersonate
gcloud iam service-accounts add-iam-policy-binding \\
  qfid-reader@acme-prod.iam.gserviceaccount.com \\
  --member='{{WORKLOAD_PROVIDER}}' \\
  --role='roles/iam.workloadIdentityUser'`;

export default function GcpDrawer({ isOpen, onClose, onConnected }) {
  const [step, setStep] = useState(1);
  const [serviceAccountEmail, setServiceAccountEmail] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Show toast (you can implement a toast system)
      console.log('Copied to clipboard');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleConnect = () => {
    setShowSuccess(true);
    onConnected();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex justify-end z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="relative w-full max-w-lg bg-background border-l border-primary/20 h-full"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-primary/10 rounded-lg transition-colors"
        >
          <X size={24} />
        </button>

        <div className="p-6 h-full flex flex-col">
          <AnimatePresence mode="wait">
            {showSuccess ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex flex-col items-center justify-center h-full"
              >
                <CheckCircle size={48} className="text-primary mb-4" />
                <h2 className="text-2xl font-semibold mb-2">Google Cloud connected!</h2>
                <p className="text-text-secondary mb-8">Your GCP environment is now connected to Quantifaid.</p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-primary text-background rounded-lg hover:bg-primary-light transition-colors"
                >
                  Done
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="steps"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex flex-col h-full"
              >
                {/* Header */}
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold mb-2">Connect Google Cloud</h2>
                  <p className="text-text-secondary">Step {step} of 2</p>
                </div>

                {/* Content */}
                <div className="flex-1">
                  {step === 1 ? (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-medium mb-2">Create & bind Service Account</h3>
                        <ol className="list-decimal list-inside space-y-2 text-sm text-text-secondary mb-4">
                          <li>Run the following gcloud command in your project.</li>
                        </ol>
                      </div>

                      <div className="bg-background-light rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-text-secondary">gcloud commands</span>
                          <button
                            onClick={() => handleCopy(GCLOUD_COMMANDS)}
                            className="p-1 hover:bg-primary/10 rounded-lg transition-colors"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                        <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
                          {GCLOUD_COMMANDS}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-medium mb-2">Enter Service Account Email</h3>
                        <p className="text-text-secondary mb-4">
                          After creating the service account, enter its email address.
                        </p>
                      </div>

                      <div className="space-y-4">
                        <input
                          type="text"
                          value={serviceAccountEmail}
                          onChange={(e) => setServiceAccountEmail(e.target.value)}
                          placeholder="qfid-reader@acme-prod.iam.gserviceaccount.com"
                          className="w-full px-4 py-2 bg-background-light border border-primary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-between pt-6 border-t border-primary/20">
                  {step === 2 && (
                    <button
                      onClick={() => setStep(1)}
                      className="flex items-center gap-2 px-4 py-2 hover:bg-primary/10 rounded-lg transition-colors"
                    >
                      <ChevronLeft size={20} />
                      Back
                    </button>
                  )}
                  <button
                    onClick={() => step === 1 ? setStep(2) : handleConnect()}
                    disabled={step === 2 && !serviceAccountEmail}
                    className={`ml-auto px-4 py-2 rounded-lg transition-colors ${
                      step === 2 && !serviceAccountEmail
                        ? 'bg-primary/20 text-text-secondary cursor-not-allowed'
                        : 'bg-primary text-background hover:bg-primary-light'
                    }`}
                  >
                    {step === 1 ? 'Next' : 'Connect'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
} 